import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/shared/api/client";
import { qk } from "@/shared/api/keys";
import { useAlerts, useUnresolvedAlerts } from "@/shared/api/hooks/alerts";
import type { LatestReadingRow } from "@/shared/api/hooks/readings";
import { useSensorNodes } from "@/shared/api/hooks/sensors";
import { useSites } from "@/shared/api/hooks/sites";
import { RISK_FRESH_MS, riToLevel, type Level } from "@/shared/lib/levelTone";
import {
  isChannelStale,
  isFresh,
  isPushStale,
  nodeHealth,
  siteHealthOf,
  type SiteHealth,
} from "@/shared/lib/health";
import { gasDisplayName, toAlertLang } from "@/shared/i18n/alertPhrases";
import type { Alert, RiskSample, SensorNode, Site } from "@/shared/api/models";
import { useLiveRiskStore, type SiteRiskView } from "./liveRiskStore";

/**
 * useControlBoard — the wallboard's data layer.
 *
 * The home screen needs, per site: nine live gas values, a fused risk index,
 * how many nodes are reporting, and how stale the newest sample is. None of
 * that comes from a single endpoint, so the merge lives here instead of in
 * the cards.
 *
 * The number of sites is not known at build time, so per-site fetching goes
 * through `useQueries` rather than a hook-per-site loop — the latter breaks
 * the rules of hooks the moment the site list grows from empty.
 *
 * Freshness is two-tier on purpose: a 30 s poll gives a correct baseline even
 * after a dropped socket, and the per-site WebSocket (see SiteSensorBridge)
 * overlays values in between. The socket is additive — if it never connects,
 * the board still updates, just more slowly.
 */

/**
 * Canonical channel order for a v3 box (3-layer enclosure, 실증화계획서 Ⅴ장).
 *
 * This is an *ordering hint*, not a whitelist. The product must ingest v1,
 * v2 and v3 sensors, and their channel sets differ — v1 is CO₂/O₂/H₂S-class,
 * v2 adds CH₄/NH₃/SO₂/NO₂/VOC, v3 is the full 3-layer box. So a card renders
 * whatever channels its nodes actually declare (`SensorNode.gases`), sorted
 * by this list, with anything unrecognised appended rather than dropped.
 */
export const GAS_ORDER = [
  "O2", "H2S", "CO", "CH4", "CO2", "NH3", "VOC", "SO2", "NO2",
] as const;
/** A channel code is just a string on the wire — no closed union. */
export type GasCode = string;

/** Sort key: known channels keep the canonical order, unknown ones follow. */
function channelRank(code: string): number {
  const i = (GAS_ORDER as readonly string[]).indexOf(code);
  return i < 0 ? GAS_ORDER.length : i;
}

/**
 * Which channels a site can show — 노드가 **선언한 것 ∪ 실제로 값이 들어온 것**.
 *
 * 예전에는 선언(`SensorNode.gases`)을 먼저 믿고, 선언이 비었을 때만(구형 노드가
 * `gases` 없이 자동 생성됨) 수신 가스로 폴백했다. 그러면 4종을 선언한 노드가
 * CO₂·연기까지 6종을 보내도 카드에는 4종만 남아 **받은 값이 화면에서 조용히
 * 사라진다**(2026-09-22 지적: 「4개가 아니라 9개 센서값이 다 보이게」). 선언만 있고
 * 값이 없는 채널은 그대로 「—」로 남아 미설치·고장을 구분하게 두고, 값이 오는
 * 채널은 선언 여부와 무관하게 보인다 — NodeTierTile 이 노드 단위로 쓰는 규칙과 같다.
 * `gases` 는 jsonb 라 구형 노드에서 객체일 수 있어 배열일 때만 읽는다.
 */
function channelsFor(nodes: SensorNode[], seen: Iterable<string>): GasCode[] {
  const set = new Set<string>();
  for (const n of nodes) {
    const raw: unknown = n.gases;
    if (Array.isArray(raw)) for (const g of raw) set.add(String(g).toUpperCase());
  }
  for (const g of seen) set.add(g.toUpperCase());
  return [...set].sort((a, b) => channelRank(a) - channelRank(b) || a.localeCompare(b));
}

/** Display symbol with proper subscripts — the API sends plain "H2S". */
export const GAS_SYMBOL: Record<string, string> = {
  O2: "O₂", H2S: "H₂S", CO: "CO", CH4: "CH₄", CO2: "CO₂",
  NH3: "NH₃", VOC: "VOC", SO2: "SO₂", NO2: "NO₂",
};

/**
 * 채널 기호 — 관제 보드 공용 규칙(NodeTierTile 이 쓰던 것을 끌어올림, 2026-09-22).
 *
 * 9종 가스는 아래첨자 기호(GAS_SYMBOL), 그 밖의 채널은 **가스 이름 사전**으로 푼다 —
 * 센서2 가 보내는 SMOKE 가 기호표에 없어 타일 셀에 날 코드 「SMOKE」가 찍혔고, 같은
 * 코드가 경보 피드에도 「SMOKE 주의」로 남아 두 자리가 서로 다른 말을 했다. 사전
 * (alertPhrases)에 있으면 「연기/Smoke」, 없으면 코드 그대로.
 *
 * 사전은 **낭독 문장용**이라 소문자다(en "smoke", vi "khói"). 셀·피드 라벨은 O₂·CO·CH₄
 * 옆에 서는 기호라 첫 글자만 대문자로 올린다. 대·소문자가 있는 글자(라틴·키릴)만 바뀌고
 * 한글·한자·타이는 그대로다. `lang` 은 i18n.language(표시 언어)를 그대로 넘긴다.
 */
export function gasSymbol(code: string, lang: string | undefined): string {
  const up = code.toUpperCase();
  const sym = GAS_SYMBOL[up];
  if (sym) return sym;
  const name = gasDisplayName(toAlertLang(lang), up);
  return /^\p{Ll}/u.test(name) ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

/**
 * 「북서 315°」처럼 8방위 + 각도. 방위만 쓰면 확산 각도를 못 읽는다.
 * 함체 타일 푸터와 기상 카드의 관측소별 줄이 같은 문자열을 쓴다.
 */
export function windDirText(deg: number | null, t: (k: string) => string): string {
  if (deg == null) return "—";
  const keys = ["n", "ne", "e", "se", "s", "sw", "w", "nw"];
  const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
  return `${t(`widgets.station.dir.${keys[i]}`)} ${Math.round(deg)}°`;
}

/**
 * How each channel can kill you. SOP-001 §1.2 groups the nine this way, and
 * the grouping is what an operator reasons with — "the toxic ones are up" is
 * a different response from "oxygen is down". The card shows it as a small
 * glyph so the category reads before the number does.
 */
export type GasHazard = "toxic" | "asphyxiant" | "flammable";
export const GAS_HAZARD: Record<string, GasHazard> = {
  CO: "toxic", H2S: "toxic", NH3: "toxic", SO2: "toxic", NO2: "toxic",
  CH4: "flammable", VOC: "flammable",
  CO2: "asphyxiant", O2: "asphyxiant",
};

export interface GasValue {
  code: GasCode;
  value: number | null;
  unit: string | null;
  level: Level;
  /** Epoch ms of the last value; null when never seen. */
  atMs: number | null;
  /**
   * 값이 온 적은 있지만 한도(GAS_STALE_SEC)를 넘게 새 값이 없다. 이때 `value` 는 null 로
   * 비우고 화면은 숫자 대신 「측정값 없음」을 그린다 — 멈춘 채널의 마지막 값을 현재 값처럼
   * 보이면 안 된다(2026-09-28). 한 번도 못 받은 채널은 false(흐린 「—」)다.
   */
  stale: boolean;
}

export interface SiteCardData {
  site: Site;
  gases: GasValue[];
  /**
   * 현장 최고 RI 의 흐름(오래된 것 → 새 것, 최대 40점). 표본마다 「그 순간 노드별 최신값 중
   * 180초 안의 최고값」을 한 점으로 — 여러 노드 표본을 그냥 늘어놓으면 노드 사이를 톱니처럼 오갔다.
   */
  riSeries: number[];
  /**
   * 현장 위험도 헤드라인(동적 RI, 0–1) — **신선한 노드 중 최고값**(2026-09-29 관제 개편).
   *
   * 예전에는 표본 목록 맨 앞 1건(= 마지막으로 보고한 아무 노드)이라 헤드라인이 노드 사이를
   * 오갔다. 지금은 노드별 최신 표본 중 180초(RISK_FRESH_MS) 안의 최고값이고, 실시간 스토어
   * (liveRiskStore — 소켓·훈련 프레임 포함)가 같거나 더 새 것을 들고 있으면 그것을 쓴다.
   * 신선한 노드가 하나도 없으면 null 이고 `riStale` 이 참이다 — 멈춘 값을 「정상」으로 보이지 않게.
   */
  ri: number | null;
  /** 헤드라인 노드(멈췄으면 가장 최근에 보고한 노드). 위험도 화면 `?node=` 재료. */
  riNodeId: number | null;
  /** 헤드라인 프레임 시각(ms). 멈췄으면 현장의 마지막 프레임 시각 — 「갱신 없음 · n분 전」 재료. */
  riAt: number | null;
  /** 위험도 표본은 있었으나 180초 넘게 새 것이 없다(`ri` 는 null). 표본이 한 번도 없으면 false. */
  riStale: boolean;
  level: Level;
  /**
   * Why the card is at `level`.
   *
   * "risk"  — the fused index itself is in that band.
   * "alert" — an open alert outranks a calmer index. The card must say so:
   *           a 심각 badge sitting above an RI of 0.06 reads as a broken
   *           gauge unless the reason is on screen.
   */
  levelSource: "risk" | "alert";
  /**
   * 규격 §3 사이트 health. `level` 은 가스가 얼마나 위험한지, 이쪽은 그 판단을
   * 믿어도 되는지를 말한다 — 센서가 전멸한 현장의 RI 0.00 은 「정상」이 아니다.
   */
  health: SiteHealth;
  /** health 가 online/warming 인 노드 수 (§3 nodes_fresh). */
  nodesFresh: number;
  nodesTotal: number;
  /** Epoch ms of the freshest reading on this site; null when nothing is in. */
  atMs: number | null;
  openAlerts: Alert[];
  loading: boolean;
}

/**
 * Values pushed over the socket, keyed site → gas → {value, at, rx}.
 * `rx` 는 이 화면이 그 채널을 **받은** 시각(브라우저 시계) — 채널 신선도는 이것으로 잰다.
 */
export type LiveMap = Map<number, Map<string, { value: number; at: number; rx: number }>>;

export interface ControlBoard {
  sites: Site[];
  nodes: SensorNode[];
  /**
   * 사이트별 `latest` 원본 행 — 노드 타일이 **노드별로** 접어 쓰기 위한 것.
   *
   * `cards[].gases` 는 사이트 전체를 가스별 최신 1건으로 이미 접어버려서
   * "어느 노드의 값인가"가 사라진다. 좌측 레일의 노드 타일은 노드마다
   * 상·중·하 3층을 따로 그려야 하므로 접기 전 행이 필요하다.
   */
  latestBySite: Map<number, LatestReadingRow[]>;
  alerts: Alert[];
  cards: SiteCardData[];
  nodesFresh: number;
  nodesTotal: number;
  /**
   * Open **gas** alerts bucketed by level index 0–3. 설비 무응답(health)은 등급 칸에 넣지 않고
   * `healthCount` 로 따로 센다 — 넣으면 LTE 가 끊길 때마다 「심각」이 떠서 가스 위험처럼 보였다(2026-09-28).
   */
  byLevel: [number, number, number, number];
  /** 열린 설비 무응답(health) 경보 수. `openCount` 에는 포함된다. */
  healthCount: number;
  openCount: number;
  todayCount: number;
  loading: boolean;
  /** 현장 목록을 서버에서 못 받았다(「현장 없음」과 구분). */
  error: boolean;
}

/**
 * Level for a single channel.
 *
 * Concentration→level belongs to the backend risk engine, which owns the
 * per-gas TWA/STEL/IDLH bands. All the card needs is "is this channel part of
 * why the site is at level N", so a channel is coloured by the open alert that
 * names it and stays neutral otherwise. The card never invents a severity the
 * engine did not assign.
 */
/**
 * 스토어와 폴링이 같은 표본을 서로 다른 시계로 옮겨 온다(스토어: 받은 순간 − 서버 나이,
 * 폴링: 서버 시각). 이만큼의 차는 「같은 때」로 보고 스토어를 쓴다.
 */
const OVERLAY_SKEW_MS = 5_000;

/** 폴링 표본(최신순) → 노드별 최신 1건 중 180초 안의 최고값, 그리고 가장 최근 표본. */
function headlineFromSamples(samples: readonly RiskSample[], nowMs: number) {
  const seen = new Set<number>();
  let best: { nodeId: number | null; ri: number; at: number } | null = null;
  let newestAt: number | null = null;
  let newestNode: number | null = null;
  for (const s of samples) {
    const key = s.node_id ?? -1;
    if (seen.has(key)) continue;
    seen.add(key);
    const at = Date.parse(s.time);
    if (!Number.isFinite(at)) continue;
    const nodeId = key < 0 ? null : key;
    if (newestAt == null || at > newestAt) {
      newestAt = at;
      newestNode = nodeId;
    }
    if (nowMs - at > RISK_FRESH_MS) continue;
    const ri = s.ri_dynamic ?? s.ri_instant;
    if (ri == null || !Number.isFinite(ri)) continue;
    if (!best || ri > best.ri || (ri === best.ri && at > best.at)) best = { nodeId, ri, at };
  }
  return { best, newestAt, newestNode };
}

/** 표본마다 「그 순간 노드별 최신값 중 180초 안의 최고값」 — 현장 헤드라인이 걸어온 길. */
function siteMaxSeries(samples: readonly RiskSample[], max = 40): number[] {
  const asc = [...samples].sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
  const lastBy = new Map<number, { ri: number; at: number }>();
  const out: number[] = [];
  for (const s of asc) {
    const at = Date.parse(s.time);
    const ri = s.ri_dynamic ?? s.ri_instant;
    if (!Number.isFinite(at) || ri == null || !Number.isFinite(ri)) continue;
    lastBy.set(s.node_id ?? -1, { ri, at });
    let m = 0;
    for (const v of lastBy.values()) if (at - v.at <= RISK_FRESH_MS && v.ri > m) m = v.ri;
    out.push(m);
  }
  return out.slice(-max);
}

/**
 * 현장 헤드라인 — 스토어(소켓·`latest`·훈련)가 폴링과 같거나 더 새 것을 들고 있으면 스토어,
 * 아니면 폴링 표본. 둘 다 신선한 값이 없으면 멈춤(표본이 있었을 때만).
 */
function siteHeadline(
  samples: readonly RiskSample[],
  store: SiteRiskView | undefined,
  nowMs: number,
): Pick<SiteCardData, "ri" | "riNodeId" | "riAt" | "riStale"> {
  const poll = headlineFromSamples(samples, nowMs);
  if (store && (poll.newestAt == null || store.updatedAt >= poll.newestAt - OVERLAY_SKEW_MS)) {
    return { ri: store.ri, riNodeId: store.nodeId, riAt: store.at, riStale: store.stale };
  }
  if (poll.best) return { ri: poll.best.ri, riNodeId: poll.best.nodeId, riAt: poll.best.at, riStale: false };
  return { ri: null, riNodeId: poll.newestNode, riAt: poll.newestAt, riStale: poll.newestAt != null };
}

function levelForGas(gas: string, alerts: Alert[]): Level {
  let worst: Level = 0;
  for (const a of alerts) {
    if (a.gas?.toUpperCase() !== gas.toUpperCase()) continue;
    const lv = Math.min(3, Math.max(0, a.level ?? 0)) as Level;
    if (lv > worst) worst = lv;
  }
  return worst;
}

/**
 * Latest-reading rows → newest row per gas across every node on the site.
 *
 * 신선한 행이 묵은 행을 먼저 이긴다 — 같은 가스를 가진 함체 중 하나만 멈췄을 때 그
 * 함체의 멈춘 값이 현장 대표값이 되면 안 된다. 신선도가 같으면 더 최근 것.
 */
function foldRows(rows: LatestReadingRow[], nowMs: number): Map<string, LatestReadingRow> {
  const out = new Map<string, LatestReadingRow>();
  for (const r of rows) {
    const key = r.gas.toUpperCase();
    const prev = out.get(key);
    if (!prev) {
      out.set(key, r);
      continue;
    }
    const rStale = isChannelStale(r, nowMs);
    const pStale = isChannelStale(prev, nowMs);
    if (rStale !== pStale ? !rStale : Date.parse(r.time) > Date.parse(prev.time)) out.set(key, r);
  }
  return out;
}

export function useControlBoard(live: LiveMap): ControlBoard {
  // Only sites that are actually being monitored. Deactivating a site is how
  // an operator retires it without losing its history — the wallboard must
  // respect that, or a decommissioned site keeps a card forever.
  const sitesQ = useSites({ is_active: true });
  const nodesQ = useSensorNodes();
  const alertsQ = useAlerts();
  /**
   * 미해제 경보 전체(상태별 page_size 500). 무필터 `useAlerts()` 는 **조직 전체 최신 50행**
   * 이라, 보드가 현장 하나로 좁혀진 뒤(2026-09-22)에는 다른 현장이 50건만 쏟아도 선택
   * 현장의 미해제 경보가 1페이지 밖으로 밀려 「활성 경보 0」·빈 피드·회색 채널이 된다 —
   * 서버엔 열린 경보가 있는데 화면은 조용한 것이 최악의 실패다. 그래서 미해제 집합을
   * 합쳐 미해제 행은 페이지에서 떨어질 수 없게 한다.
   * 요청은 늘지 않는다: 같은 쿼리키를 AppShell 의 useAlarmLight·AlertTtsAnnouncer·
   * AlarmCenter 가 항상 잡고 있어 캐시 공유다.
   */
  const unresolved = useUnresolvedAlerts();

  const sites = useMemo(() => sitesQ.data?.results ?? [], [sitesQ.data]);

  /**
   * Nodes and alerts must be scoped to the sites actually on the board.
   *
   * The node and alert endpoints are not site-filtered, so a decommissioned
   * site keeps contributing: its nodes inflate "online x/y" and its alerts
   * fill the feed with rows the board cannot even name (the site lookup
   * misses, so they render as "#4"). Scope them here, once.
   */
  const siteIds = useMemo(() => new Set(sites.map((s) => s.id)), [sites]);
  const nodes = useMemo(
    () => (nodesQ.data?.results ?? []).filter((n) => siteIds.has(n.site)),
    [nodesQ.data, siteIds],
  );
  const alerts = useMemo(() => {
    // 미해제 집합을 먼저 깔고 최신 페이지로 덮는다 — 방금 해제된 행은 최신 페이지에만
    // cleared 로 있고 미해제 집합엔 아직 open 으로 남아 있을 수 있어, 페이지 쪽이 이겨야
    // 해제가 화면에 바로 반영된다. 정렬은 소비처(AlertDigestStrip 의 마지막 경보 등)가 raised_at 으로 다시 한다.
    const byId = new Map<number, Alert>();
    for (const a of unresolved.alerts) if (a.id != null) byId.set(a.id, a);
    for (const a of alertsQ.data?.results ?? []) if (a.id != null) byId.set(a.id, a);
    return [...byId.values()].filter((a) => siteIds.has(a.site));
  }, [alertsQ.data, unresolved.alerts, siteIds]);

  // Per-site fan-out. `useQueries` takes a dynamic array, which is what lets
  // the site count change without violating hook order.
  const readings = useQueries({
    queries: sites.map((s) => ({
      queryKey: ["iot", "readings", "latest", s.id] as const,
      queryFn: () =>
        api
          .get<LatestReadingRow[]>("/iot/readings/latest", { params: { site: s.id } })
          .then((r) => {
            // 받아 온 순간을 찍는다 — 같은 queryKey 를 쓰는 useLatestReadings 와 같은 모양(isChannelStale 참조).
            const at = Date.now();
            return Array.isArray(r.data) ? r.data.map((row) => ({ ...row, fetched_at_ms: at })) : [];
          }),
      refetchInterval: 30_000,
      // No `placeholderData` here. Inside useQueries a `(prev) => prev`
      // placeholder is re-evaluated every render and bumps `dataUpdatedAt`,
      // which feeds the signature below, which recomputes the cards, which
      // re-renders — a loop that locks the renderer. React Query already
      // keeps the last data during a background refetch, so the placeholder
      // bought nothing.
    })),
  });

  const risks = useQueries({
    queries: sites.map((s) => ({
      queryKey: qk.safety.risk.list({ site: s.id }),
      queryFn: () =>
        api
          // `site_id`, not `site`: RiskSample keeps the site as a plain integer
          // column rather than a FK, so that is the only name the filter set
          // accepts. `?site=` was silently ignored — every card then showed the
          // *same* fused RI (whatever the newest sample in the org happened to
          // be) and the KPI credited the peak to the wrong site.
          .get<{ results: RiskSample[] }>("/risk-samples/", { params: { site_id: s.id } })
          .then((r) => r.data),
      refetchInterval: 30_000,
    })),
  });

  // React Query hands back fresh wrapper objects every render, so depending on
  // `readings`/`risks` directly would defeat the memo. Spreading their data
  // into the dependency array is worse still — the array would change length
  // whenever a site is added, which React explicitly forbids. So collapse both
  // fan-outs into fixed-width signature strings that change exactly when a
  // fetch lands.
  const readingSig = readings.map((q) => `${q.dataUpdatedAt}/${q.isPending ? 1 : 0}`).join("|");
  const riskSig = risks.map((q) => q.dataUpdatedAt).join("|");
  // 실시간 위험도(SiteRiskBridge → liveRiskStore) — 현장별 헤드라인 Map **참조**만 꺼낸다
  // (zustand 5 는 셀렉터 결과를 === 로 비교한다 — 셀렉터에서 새 객체를 만들면 렌더가 끝없이 돈다).
  // 값이 같으면 스토어가 같은 참조를 유지하므로 헛된 재계산은 없다.
  const liveSites = useLiveRiskStore((s) => s.sites);

  const cards = useMemo(() => {
    // 채널 신선도 기준 시각. 이 memo 는 푸시 반영(250ms 묶음)·30초 폴링마다 다시 돌므로
    // 초 단위 시계를 deps 에 넣지 않아도 판정이 한도보다 크게 늦지 않는다.
    const nowMs = Date.now();
    return sites.map((site, i): SiteCardData => {
      const rows = foldRows(readings[i]?.data ?? [], nowMs);
      const pushed = live.get(site.id);
      const openAlerts = alerts.filter((a) => a.site === site.id && a.state !== "cleared");

      const siteNodes = nodes.filter((n) => n.site === site.id);
      const channels = channelsFor(siteNodes, rows.keys());

      const gases: GasValue[] = channels.map((code) => {
        const row = rows.get(code);
        const push = pushed?.get(code);
        const rowAt = row ? Date.parse(row.time) : null;
        // 마지막 수신 시각은 신선도와 무관하게 더 최근 쪽이다(카드의 「n초 전」 재료).
        const at = push != null && (rowAt == null || push.at >= rowAt) ? push.at : rowAt;
        // ⛔ 값은 **신선한** 쪽에서만 가져온다. 행은 서버 판정(stale), 푸시는 받은 시각으로
        //    잰다 — 채널이 멈춰도 폴링은 24시간 동안 마지막 값을 주고, LiveMap 은 마지막
        //    푸시를 영원히 쥐고 있다. 둘 다 신선하면 더 최근 것(대개 소켓)이 이긴다.
        const rowOk = row != null && !isChannelStale(row, nowMs);
        const pushOk = push != null && !isPushStale(push.rx, nowMs);
        const usePush = pushOk && (!rowOk || rowAt == null || push.at >= rowAt);
        const raw = usePush ? push.value : rowOk ? Number(row.value) : null;
        const value = raw != null && Number.isFinite(raw) ? raw : null;
        return {
          code,
          value,
          unit: row?.unit ?? null,
          level: levelForGas(code, openAlerts),
          atMs: at,
          stale: value == null && (row != null || push != null),
        };
      });

      const stamps = gases.map((g) => g.atMs).filter((a): a is number => a != null);
      // ⛔ `samples[0]` 을 현장 RI 로 쓰지 않는다 — 그건 마지막으로 보고한 **아무 노드**다
      //    (헤드라인이 노드 사이를 오가던 원인). 노드별 최신값 중 신선한 최고값 + 스토어 덧씌움.
      const samples = risks[i]?.data?.results ?? [];
      const riSeries = siteMaxSeries(samples);
      const headline = siteHeadline(samples, liveSites.get(site.id), nowMs);
      const ri = headline.ri;

      // Show the worse of "what the engine scored" and "what is actually
      // alarming" — an open critical alert must never sit behind a stale,
      // calmer RI sample.
      // ⛔ 설비 무응답(health)은 위험 등급에 넣지 않는다 — 가스 위험이 아니라 통신 상태다(2026-09-28).
      const alertLevel = openAlerts.reduce<Level>(
        (m, a) => (a.kind === "health" ? m : Math.max(m, Math.min(3, Math.max(0, a.level ?? 0))) as Level),
        0,
      );
      const riskLevel = riToLevel(ri);
      const level = Math.max(riskLevel, alertLevel) as Level;

      return {
        site,
        gases,
        riSeries,
        ...headline,
        level,
        levelSource: alertLevel > riskLevel ? "alert" : "risk",
        // `status` 문자열은 되돌려 쓰는 코드가 없어 영구 online 이었다. 판정은
        // 서버 health 또는 마지막 수신 시각으로만 한다 (§2).
        health: siteHealthOf(site, siteNodes),
        nodesFresh: siteNodes.filter((n) => isFresh(nodeHealth(n))).length,
        nodesTotal: siteNodes.length,
        atMs: stamps.length ? Math.max(...stamps) : null,
        openAlerts,
        loading: readings[i]?.isPending === true && !readings[i]?.data,
      };
    });
    // `readings`/`risks` are read inside but intentionally absent from the
    // deps: their identity churns every render, and the signatures above are
    // what actually track their contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, nodes, alerts, live, readingSig, riskSig, liveSites]);

  // 카드와 같은 fan-out 결과를 노드 단위로도 쓰게 원본 행을 사이트별로 묶어 둔다.
  // deps 가 `readings` 가 아니라 `readingSig` 인 이유는 cards 와 같다.
  const latestBySite = useMemo(() => {
    const out = new Map<number, LatestReadingRow[]>();
    sites.forEach((s2, i) => out.set(s2.id, readings[i]?.data ?? []));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, readingSig]);

  const open = useMemo(() => alerts.filter((a) => a.state !== "cleared"), [alerts]);

  return useMemo(() => {
    const byLevel: [number, number, number, number] = [0, 0, 0, 0];
    let healthCount = 0;
    for (const a of open) {
      if (a.kind === "health") healthCount += 1;
      else byLevel[Math.min(3, Math.max(0, a.level ?? 0))] += 1;
    }

    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);

    return {
      sites,
      nodes,
      alerts,
      cards,
      latestBySite,
      nodesFresh: nodes.filter((n) => isFresh(nodeHealth(n))).length,
      nodesTotal: nodes.length,
      byLevel,
      healthCount,
      openCount: open.length,
      todayCount: alerts.filter(
        (a) => a.raised_at && Date.parse(a.raised_at) >= midnight.getTime(),
      ).length,
      loading: sitesQ.isPending || nodesQ.isPending,
      /** 현장 목록을 서버에서 못 받았다 — 「등록된 현장 없음」과 다르다(회선 불량을 빈 현장으로 보이면 안 된다). */
      error: sitesQ.isError && !sitesQ.data,
    };
  }, [sites, nodes, alerts, cards, latestBySite, open, sitesQ.isPending, nodesQ.isPending, sitesQ.isError, sitesQ.data]);
}

/**
 * A clock that advances once a second, for "n초 전" labels.
 *
 * Deliberately *not* part of the board memo: putting the current time in
 * there rebuilt every card once a second, nine chips and two SVGs apiece,
 * to move one line of text. Components that show elapsed time subscribe to
 * this instead and re-render on their own.
 */
export function useNowTick(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}


/**
 * 「n초 전 / n분 전 / n시간 전」 — 언어별 키(`pages.control.agoSec/agoMin/agoHour`)로.
 * 예전 `ageAmount` 는 단위가 한글이라 영어 화면에서 「127시간 ago」가 됐다(2026-09-22 리뷰) —
 * 그래서 지웠다. KpiStrip 의 formatAgo 와 같은 키다.
 */
export function ageText(ms: number | null, t: TFunction): string {
  if (ms == null) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return t("pages.control.agoSec", { n: s });
  const m = Math.floor(s / 60);
  if (m < 60) return t("pages.control.agoMin", { n: m });
  return t("pages.control.agoHour", { n: Math.floor(m / 60) });
}
